import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/ui/notify';
import {
  getTierStyling,
  normalizeTier,
  tierToSelectValue,
} from '@/lib/accountTier';
import { useTranslation } from '@/components/utils/translations';

// [2026-10-29] i18n: display only. Tier values ('Tier 2' / 'T2') still drive styling and the API;
// only the visible word 'Tier' is swapped for the UI language's word.
const localiseTier = (label, t) =>
  typeof label === 'string' ? label.replace(/^Tier\b/, t('tier')) : label;

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

const TIER_OPTIONS = [
  { value: 'T1', label: 'Tier 1', badgeClass: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'T2', label: 'Tier 2', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'T3', label: 'Tier 3', badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'T4', label: 'Tier 4', badgeClass: 'bg-gray-100 text-gray-800 border-gray-200' },
];

export default function AccountTierSelect({
  accountId,
  entityId,
  tier: tierProp,
  tenantId,
  userId,
  onTierUpdate,
  disabled = false,
  triggerClassName = 'h-7 w-auto min-w-[80px] border-0 p-0 hover:opacity-80',
}) {
  const { t } = useTranslation();
  const [displayTier, setDisplayTier] = useState(normalizeTier(tierProp));
  const [isUpdating, setIsUpdating] = useState(false);
  const selectValue = tierToSelectValue(tierProp) || tierToSelectValue(displayTier);

  useEffect(() => {
    setDisplayTier(normalizeTier(tierProp));
  }, [tierProp]);

  const handleTierChange = async (newTier) => {
    if (!accountId || !tenantId) return;
    setIsUpdating(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId,
        ...(userId ? { 'x-user-id': userId } : {}),
      };
      const payload = { tenant_id: tenantId, tier: newTier };
      const apiRecordId = encodeURIComponent(accountId);
      const entityRecordId = encodeURIComponent(entityId || accountId);

      let response = await fetch(`${API_BASE_URL}/api/analytics-v2/accounts/${apiRecordId}/tier`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      let updatedTier = newTier;

      if (response.ok) {
        const data = await response.json();
        updatedTier = data.account?.account_tier ?? data.account?.tier ?? newTier;
      } else if (response.status === 404 || response.status >= 500) {
        const entityRes = await fetch(`${API_BASE_URL}/api/entities/AccountFinal/${entityRecordId}`, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ tier: newTier, account_tier: newTier }),
        });
        if (entityRes.ok) {
          const updated = await entityRes.json();
          updatedTier = updated.account_tier ?? updated.tier ?? newTier;
        } else {
          const error = await entityRes.json().catch(() => ({}));
          notify({
            title: 'Error',
            description: error.error || error.message || 'Failed to update tier',
            variant: 'destructive',
          });
          return;
        }
      } else {
        const error = await response.json().catch(() => ({}));
        notify({
          title: 'Error',
          description: error.error || error.message || 'Failed to update tier',
          variant: 'destructive',
        });
        return;
      }

      const normalized = normalizeTier(updatedTier);
      setDisplayTier(normalized);
      onTierUpdate?.(updatedTier);
      try {
        sessionStorage.removeItem('market-cache-v2');
      } catch {
        /* ignore */
      }
      notify({
        title: 'Tier Updated',
        description: `Account tier changed to ${newTier}`,
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating tier:', error);
      notify({
        title: 'Error',
        description: 'Failed to update tier',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const badgeTier = selectValue || displayTier;

  return (
    <Select
      value={selectValue || undefined}
      onValueChange={handleTierChange}
      disabled={disabled || isUpdating}
    >
      <SelectTrigger className={triggerClassName} onClick={(e) => e.stopPropagation()}>
        <SelectValue placeholder="—">
          {isUpdating ? (
            <Badge className="px-2 py-1 text-xs font-semibold flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              ...
            </Badge>
          ) : selectValue ? (
            <Badge className={`px-2 py-1 text-xs font-semibold border ${getTierStyling(badgeTier)}`}>
              {localiseTier(displayTier !== 'N/A' ? displayTier : selectValue, t)}
            </Badge>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {TIER_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            <Badge className={`${opt.badgeClass} border`}>{localiseTier(opt.label, t)}</Badge>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
